import { skipToken } from '@reduxjs/toolkit/query/react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectTentativeETALocation } from '@rsos/reduxDataStore/src/features/airAmbulance';
import { useGetTransportTentativeEtaQuery } from '@rsos/reduxDataStore/src/features/resourceRequests';
import { TentativeETA } from '../../types';
import {
  formatTentativeEtaFromIso,
  formatTransportEtaDisplay,
} from '../etlStatusUtils';
import { useAirAmbulanceEtlStatus } from './useAirAmbulanceEtlStatus';

/**
 * Return the latest ETA information that is available.
 *
 * If we already have a request selected and the ETA is available from the
 * status information, we use the ETA provided by GMR in the status update.
 * Otherwise we fetch the tentative ETA from GMR using the etl-integrations
 * service. The "tentative ETA" is an estimate GMR provides when a transport
 * hasn't been dispatched yet.
 */
export function useAirAmbulanceEtlEta(): TentativeETA | null {
  const tentativeETALocation = useSelector(selectTentativeETALocation);
  const { latestProvider, latestEta, latestStatus } =
    useAirAmbulanceEtlStatus();
  const statusEtaDisplay = formatTransportEtaDisplay(latestEta);
  // Tentative ETA only applies before any GMR status rows exist.
  const hasStatusUpdates = latestStatus != null;
  const shouldFetchEta =
    !latestEta && !hasStatusUpdates && Boolean(tentativeETALocation);
  const queryLocation = useMemo(() => {
    if (!shouldFetchEta || !tentativeETALocation) {
      return skipToken;
    }

    const decimalPlaces = 5;

    return {
      latitude: Number(tentativeETALocation.latitude.toFixed(decimalPlaces)),
      longitude: Number(tentativeETALocation.longitude.toFixed(decimalPlaces)),
    };
  }, [shouldFetchEta, tentativeETALocation]);

  const { data } = useGetTransportTentativeEtaQuery(queryLocation);

  if (statusEtaDisplay) {
    // Case where we get the ETA from status updates of an already dispatched
    // transport.  The label reflects whether the transport has already arrived.
    return {
      display: statusEtaDisplay,
      providers: latestProvider?.name || null,
    };
  }

  if (data && !hasStatusUpdates) {
    // Case where we don't have a status update from a dispatched transport,
    // and we had to query GMR to get a tentative ETA (i.e. estimate).
    // Label should always reflect that it is tentative.
    return {
      display: formatTentativeEtaFromIso(data.eta),
      providers: data.providers,
    };
  }

  return null;
}
